import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IoCardOutline,
  IoCheckmarkCircle,
  IoTimeOutline,
  IoCalendarOutline,
  IoChevronBackOutline,
  IoChevronForwardOutline,
  IoAlertCircleOutline,
  IoSchoolOutline,
  IoInformationCircleOutline,
} from "react-icons/io5";
import ProgressBar from "../components/ProgressBar";
import InstallmentSkeleton from "../components/skeleton-loading/InstallmentSkeleton";

import { useData } from "../data/DataProvider";
import cardLogos from "../assets/card-logos.png";

export default function Installment() {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    paymentSummaryData,
    paymentHistoryData,
    installmentsSummaryData,
    activeRouteData,
    studentData,
    getAllData,
    loading,
  } = useData();

  const [currentRouteIndex, setCurrentRouteIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedInstallment, setSelectedInstallment] = useState({
    title: "",
    amount: "",
    rawItem: null,
  });

  const handleOpenModal = (title, amount, rawItem) => {
    setSelectedInstallment({ title, amount, rawItem });
    setIsModalOpen(true);
  };

  const handleProceedToCheckout = (installmentParam = null, routeParam = null) => {
    setIsModalOpen(false);

    const targetRoute = routeParam || activeApiRoute;
    const transaction = installmentParam;
    const rawItem = transaction?.rawItem || installmentParam || selectedInstallment?.rawItem;
    const title =
      transaction?.title ||
      (rawItem?.installment_number
        ? `${rawItem.installment_number}. Taksit Ödemesi`
        : selectedInstallment?.title);

    const amount = Number(
      rawItem?.balance ?? (Number(rawItem?.amount || 0) - Number(rawItem?.paid_amount || 0))
    );

    const netAmount =
      rawItem?.net_amount !== undefined ? Number(rawItem.net_amount) : null;
    const taxAmount =
      rawItem?.tax_amount !== undefined ? Number(rawItem.tax_amount) : null;

    const studentObj = studentData || targetRoute || installmentsSummaryData;
    const firstName = studentObj?.name || "";
    const lastName = studentObj?.surname || "";
    const fullStudentName = `${firstName} ${lastName}`.trim() || studentFullName || "-";

    const detectedSchoolName =
      targetRoute?.route_name ||
      studentObj?.school_name ||
      studentObj?.school?.name ||
      targetRoute?.school_name ||
      targetRoute?.school?.name ||
      "-";

    const dueDateFormatted = rawItem?.due_date
      ? new Date(rawItem.due_date).toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
      : transaction?.date || "Belirtilmedi";

    navigate("/checkout", {
      state: {
        userName: fullStudentName,
        schoolName: detectedSchoolName,
        routeName: targetRoute?.route_name || "Servis Rotası Belirtilmedi",
        installmentMonth: `${title} (${dueDateFormatted})`,
        serviceAmount: amount,
        netAmount: netAmount,
        taxAmount: taxAmount,
        paymentDueDate: transaction?.date || dueDateFormatted || "Belirtilmedi",
        rawItem: rawItem,
        posData: { rawItem: rawItem },
      },
    });
  };

  useEffect(() => {
    const forceRefresh = Boolean(location.state?.forceRefresh);
    getAllData(forceRefresh);
  }, [location.state]);

  useEffect(() => {
    const routes = Array.isArray(installmentsSummaryData)
      ? installmentsSummaryData
      : [];
    if (routes.length > 0) {
      const unpaidIndex = routes.findIndex((r) =>
        (r.installments || []).some(
          (ins) => ins.status === "unpaid" || ins.status === "partial",
        ),
      );
      if (unpaidIndex !== -1) {
        setCurrentRouteIndex(unpaidIndex);
      } else if (activeRouteData?.student_route_id) {
        const foundIndex = routes.findIndex(
          (r) => r.student_route_id === activeRouteData.student_route_id,
        );
        if (foundIndex !== -1) {
          setCurrentRouteIndex(foundIndex);
        }
      }
    }
  }, [activeRouteData, installmentsSummaryData]);

  const routesList = Array.isArray(installmentsSummaryData)
    ? installmentsSummaryData
    : [];
  const activeApiRoute = routesList[currentRouteIndex] || null;
  const allInstallments = activeApiRoute?.installments || [];

  const studentFullName =
    activeApiRoute?.student_name ||
    activeRouteData?.student_name ||
    (studentData?.name && studentData?.surname
      ? `${studentData.name} ${studentData.surname}`
      : studentData?.name || "");

  const currentSummary = Array.isArray(paymentSummaryData)
    ? paymentSummaryData.find(
      (s) => s.student_route_id === activeApiRoute?.student_route_id,
    ) || paymentSummaryData[0]
    : paymentSummaryData;

  const isRouteOverdue = currentSummary?.is_overdue || false;

  const handlePrevRoute = () => {
    if (currentRouteIndex > 0) {
      setCurrentRouteIndex(currentRouteIndex - 1);
    }
  };

  const handleNextRoute = () => {
    if (currentRouteIndex < routesList.length - 1) {
      setCurrentRouteIndex(currentRouteIndex + 1);
    }
  };

  const totalCount = allInstallments.length;
  const paidCount = allInstallments.filter(
    (ins) => ins.status === "paid",
  ).length;
  const progressPercentage =
    totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

  const remainingCalc = allInstallments
    .filter((i) => i.status !== "paid")
    .reduce(
      (sum, item) =>
        sum +
        Number(
          item.balance ?? (Number(item.amount || 0) - Number(item.paid_amount || 0))
        ),
      0
    );

  const remainingAmount = `₺${remainingCalc.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
  })}`;

  const nextUnpaidInstallment = allInstallments.find(
    (ins) => ins.status === "unpaid" || ins.status === "partial",
  );
  const nextPaymentDateFormatted = nextUnpaidInstallment?.due_date
    ? new Date(nextUnpaidInstallment.due_date).toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
    })
    : "Tamamlandı";

  useEffect(() => {
    const autoOpenPayment =
      location.state?.autoOpenPay || location.state?.autoOpenPayment;
    const targetRouteId = location.state?.targetRouteId;
    const targetInstallmentId = location.state?.targetInstallmentId;

    if (
      (autoOpenPayment || targetRouteId || targetInstallmentId) &&
      !loading &&
      installmentsSummaryData
    ) {
      const routes = Array.isArray(installmentsSummaryData)
        ? installmentsSummaryData
        : [];

      let targetRouteIdx = -1;

      if (targetRouteId) {
        targetRouteIdx = routes.findIndex((r) => {
          const rId = r?.student_route_id || r?.route_id || r?.id;
          return rId && String(rId) === String(targetRouteId);
        });
      }

      if (targetRouteIdx === -1 && targetInstallmentId) {
        targetRouteIdx = routes.findIndex((r) =>
          (r.installments || []).some(
            (i) =>
              (i.status === "unpaid" || i.status === "partial") &&
              (String(i.id) === String(targetInstallmentId) ||
                String(i.installment_number) === String(targetInstallmentId)),
          ),
        );
      }

      if (targetRouteIdx === -1) {
        targetRouteIdx = routes.findIndex((r) =>
          (r.installments || []).some(
            (i) => i.status === "unpaid" || i.status === "partial",
          ),
        );
      }

      if (targetRouteIdx === -1) {
        targetRouteIdx = currentRouteIndex;
      }

      if (targetRouteIdx !== currentRouteIndex) {
        setCurrentRouteIndex(targetRouteIdx);
      }

      const activeRoute = routes[targetRouteIdx] || routes[0];
      const installments = activeRoute?.installments || [];

      let targetItem = null;

      if (targetInstallmentId) {
        targetItem =
          installments.find(
            (i) =>
              (i.status === "unpaid" || i.status === "partial") &&
              (String(i.id) === String(targetInstallmentId) ||
                String(i.installment_number) === String(targetInstallmentId)),
          ) ||
          installments.find(
            (i) =>
              String(i.id) === String(targetInstallmentId) ||
              String(i.installment_number) === String(targetInstallmentId),
          );
      }

      if (!targetItem) {
        targetItem = installments.find(
          (i) => i.status === "unpaid" || i.status === "partial",
        );
      }

      if (targetItem) {
        const formattedDate = new Date(targetItem.due_date).toLocaleDateString(
          "tr-TR",
          {
            day: "numeric",
            month: "long",
            year: "numeric",
          },
        );
        const remainingValue = Number(
          targetItem.balance ??
          (Number(targetItem.amount || 0) - Number(targetItem.paid_amount || 0)),
        );
        const formattedAmountStr = `₺${remainingValue.toLocaleString("tr-TR", {
          minimumFractionDigits: 2,
        })}`;

        const trans = {
          id: targetItem.installment_number,
          type: targetItem.status === "partial" ? "partial" : "waiting",
          title: `${targetItem.installment_number}. Taksit Ödemesi`,
          date: formattedDate,
          amount: formattedAmountStr,
          rawItem: targetItem,
        };

        navigate(location.pathname, { replace: true, state: {} });
        handleProceedToCheckout(trans, activeRoute);
      }
    }
  }, [location.state, loading, installmentsSummaryData]);

  let foundFirstUnpaid = false;

  const formattedTransactions = allInstallments.map((item) => {
    const dateObj = new Date(
      item.status === "paid" && item.paid_at ? item.paid_at : item.due_date,
    );
    const formattedDate =
      item.due_date || item.paid_at
        ? dateObj.toLocaleDateString("tr-TR", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
        : "-";

    const totalAmount = Number(item.amount || 0);
    const paidAmount = Number(item.paid_amount || 0);
    const remainingBalance = Number(
      item.balance ?? (totalAmount - paidAmount),
    );

    const isPartial =
      item.status === "partial" ||
      (paidAmount > 0 && remainingBalance > 0 && item.status !== "paid");

    if (item.status === "paid") {
      return {
        id: item.installment_number,
        type: "paid",
        title: `${item.installment_number}. Taksit Ödemesi`,
        date: formattedDate,
        amount: `₺${totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
        rawItem: item,
      };
    }

    if (isPartial) {
      if (!foundFirstUnpaid) foundFirstUnpaid = true;
      return {
        id: item.installment_number,
        type: "partial",
        title: `${item.installment_number}. Taksit Ödemesi`,
        date: formattedDate,
        amount: `₺${remainingBalance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
        rawItem: item,
      };
    }

    if (item.status === "unpaid" && !foundFirstUnpaid) {
      foundFirstUnpaid = true;
      return {
        id: item.installment_number,
        type: "waiting",
        title: `${item.installment_number}. Taksit Ödemesi`,
        date: formattedDate,
        amount: `₺${remainingBalance.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
        rawItem: item,
      };
    }

    return {
      id: item.installment_number,
      type: "future",
      title: `${item.installment_number}. Taksit Ödemesi`,
      date: formattedDate,
      amount: `₺${totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`,
      rawItem: item,
    };
  });

  if (loading && !installmentsSummaryData) {
    return <InstallmentSkeleton />;
  }

  return (
    <>
      <div className="min-h-screen bg-[#f8f9ff] pt-6 pb-12 antialiased">
        <div className="max-w-3xl text-[#00306b] mx-auto px-4 md:mt-8 space-y-6">
          <div className="flex items-center justify-between p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
            {routesList.length > 1 && (
              <button
                onClick={handlePrevRoute}
                disabled={currentRouteIndex === 0}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 cursor-pointer"
              >
                <IoChevronBackOutline size={24} />
              </button>
            )}

            <div className="flex-1 text-center px-4">
              <h2 className="text-base md:text-lg font-bold tracking-wide text-[#00306b] truncate">
                {activeApiRoute?.route_name || "Güzergah Bilgisi Yok"}
              </h2>
            </div>

            {routesList.length > 1 && (
              <button
                onClick={handleNextRoute}
                disabled={currentRouteIndex === routesList.length - 1}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 cursor-pointer"
              >
                <IoChevronForwardOutline size={24} />
              </button>
            )}
          </div>

          <div className="bg-[#00306b] text-white rounded-2xl p-5 shadow-md border border-white/5">
            {studentFullName ? (
              <div className="flex items-center pb-3.5 mb-4 border-b border-white/20">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center mr-3 flex-shrink-0">
                  <IoSchoolOutline size={20} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-blue-100 text-xs font-semibold uppercase tracking-wider">
                    Öğrenci
                  </p>
                  <p className="text-white text-xl font-bold mt-0.5 truncate">
                    {studentFullName}
                  </p>
                </div>
              </div>
            ) : null}

            <div>
              <p className="text-white text-base font-medium">İlerleme</p>
              <div className="flex justify-between items-baseline mt-1">
                <h3 className="text-xl font-bold">
                  {paidCount} / {totalCount} Taksit
                </h3>
                <span className="text-2xl font-black">
                  %{progressPercentage}
                </span>
              </div>
              <ProgressBar
                barColor="#FFFFFF"
                trackColor="#FFFFFF33"
                percentage={progressPercentage}
                showPercentage={false}
              />
            </div>

            <div className="border-t border-white/10 my-4"></div>

            <div className="grid grid-cols-2 gap-2">
              <div className="text-center">
                <p className="text-white font-medium">Kalan Tutar</p>
                <p className="text-lg font-bold mt-0.5">{remainingAmount}</p>
              </div>
              <div className="text-center">
                <p className="text-white font-medium">Sıradaki Ödeme</p>
                <p className="text-lg font-bold mt-0.5">
                  {nextPaymentDateFormatted}
                </p>
              </div>
            </div>
          </div>

          {/* Ödeme Bilgilendirmesi Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start space-x-3">
            <IoInformationCircleOutline className="text-2xl text-blue-900 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-blue-900 font-semibold text-base mb-1">
                Ödeme Bilgilendirmesi
              </h4>
              <p className="text-blue-800 text-sm leading-relaxed">
                Taksit ödemelerinizi mobil uygulamanızdan, şubelerimizden veya banka havalesi (EFT/Havale) yoluyla gerçekleştirebilirsiniz. Detaylı bilgi için servis sorumlusu ile iletişime geçebilirsiniz.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center px-1">
              <h2 className="text-xl font-bold text-gray-900">
                Ödeme Takvimi
              </h2>
            </div>

            {formattedTransactions.length > 0 ? (
              formattedTransactions.map((transaction, index) => {
                if (transaction.type === "paid") {
                  return (
                    <div
                      key={transaction.id || index}
                      className="bg-white rounded-xl p-4 border border-gray-100 shadow-[0px_2px_8px_rgba(0,0,0,0.01)] flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center text-green-600 text-lg flex-shrink-0">
                          <IoCheckmarkCircle className="text-2xl" />
                        </div>
                        <div>
                          <h4 className="text-gray-800 font-semibold">
                            {transaction.title}
                          </h4>
                          <p className="text-gray-500 text-sm mt-0.5">
                            {transaction.date}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-800">
                          {transaction.amount}
                        </p>
                        <span className="text-[10px] font-bold text-green-600 tracking-wider block mt-0.5">
                          ÖDENDİ
                        </span>
                      </div>
                    </div>
                  );
                }

                if (transaction.type === "partial") {
                  return (
                    <div
                      key={transaction.id || index}
                      className="bg-white rounded-xl border-2 border-amber-500 shadow-sm overflow-hidden transition-colors"
                    >
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center text-lg flex-shrink-0">
                            <IoCardOutline className="text-2xl" />
                          </div>
                          <div>
                            <h4 className="text-gray-900 font-semibold">
                              {transaction.title}
                            </h4>
                            <p className="text-gray-500 text-sm mt-0.5">
                              {transaction.date}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {transaction.amount}
                          </p>
                          <span className="text-[10px] font-bold tracking-wider block mt-0.5 text-amber-600">
                            KISMİ ÖDEME
                          </span>
                        </div>
                      </div>

                      <div className="px-4 pb-4">
                        <button
                          onClick={() => handleProceedToCheckout(transaction)}
                          className="w-full text-white py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm bg-amber-600 hover:bg-amber-700 cursor-pointer"
                        >
                          <IoCardOutline className="text-base" />
                          Şimdi Öde
                        </button>
                      </div>
                    </div>
                  );
                }

                if (transaction.type === "waiting") {
                  return (
                    <div
                      key={transaction.id || index}
                      className={`bg-white rounded-xl border-2 shadow-sm overflow-hidden transition-colors ${isRouteOverdue
                          ? "border-red-500 bg-red-50/10"
                          : "border-[#0052cc]"
                        }`}
                    >
                      <div className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-12 h-12 rounded-full flex items-center justify-center text-lg flex-shrink-0 ${isRouteOverdue
                                ? "bg-red-50 text-red-600"
                                : "bg-blue-50 text-blue-600"
                              }`}
                          >
                            {isRouteOverdue ? (
                              <IoAlertCircleOutline className="text-2xl animate-pulse" />
                            ) : (
                              <IoCardOutline className="text-2xl" />
                            )}
                          </div>
                          <div>
                            <h4 className="text-gray-900 font-semibold">
                              {transaction.title}
                            </h4>
                            <p className="text-gray-500 text-sm mt-0.5">
                              {transaction.date}
                            </p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {transaction.amount}
                          </p>
                          <span
                            className={`text-[10px] font-bold tracking-wider block mt-0.5 ${isRouteOverdue ? "text-red-600" : "text-[#0052cc]"
                              }`}
                          >
                            {isRouteOverdue
                              ? "GECİKMİŞ ÖDEME"
                              : "BEKLEYEN ÖDEME"}
                          </span>
                        </div>
                      </div>

                      <div className="px-4 pb-4">
                        <button
                          onClick={() => handleProceedToCheckout(transaction)}
                          className={`w-full text-white py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors shadow-sm cursor-pointer ${isRouteOverdue
                              ? "bg-red-600 hover:bg-red-700"
                              : "bg-[#0052cc] hover:bg-[#0046b3]"
                            }`}
                        >
                          <IoCardOutline className="text-base" />
                          {isRouteOverdue
                            ? "Gecikmiş Ödemeyi Yap"
                            : "Şimdi Öde"}
                        </button>
                      </div>
                    </div>
                  );
                }

                if (transaction.type === "future") {
                  return (
                    <div
                      key={transaction.id || index}
                      className="bg-white/90 rounded-xl p-4 border border-gray-100 shadow-[0px_2px_8px_rgba(0,0,0,0.01)] flex items-center justify-between opacity-65"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 text-lg flex-shrink-0">
                          <IoTimeOutline className="text-2xl" />
                        </div>
                        <div>
                          <h4 className="text-gray-700 font-semibold">
                            {transaction.title}
                          </h4>
                          <p className="text-gray-400 text-sm mt-0.5">
                            {transaction.date}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-700">
                          {transaction.amount}
                        </p>
                        <span className="text-[10px] font-bold text-gray-400 tracking-wider block mt-0.5">
                          GELECEK ÖDEME
                        </span>
                      </div>
                    </div>
                  );
                }

                return null;
              })
            ) : (
              <div className="py-6 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                <p className="text-center text-gray-400 italic text-sm">
                  Ödeme planı verisi bulunamadı.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div
            className="absolute inset-0"
            onClick={() => setIsModalOpen(false)}
          ></div>
          <div className="bg-white rounded-[32px] p-8 max-w-sm w-full text-center shadow-2xl relative z-10 flex flex-col items-center">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl mb-5 ${isRouteOverdue
                  ? "bg-red-50 text-red-600"
                  : "bg-[#f0f5ff] text-[#186ade]"
                }`}
            >
              <IoCardOutline size={28} />
            </div>

            <h3 className="text-2xl font-bold text-gray-900 tracking-tight">
              {isRouteOverdue ? "Gecikmiş Ödeme" : "Ödeme Yap"}
            </h3>
            <p className="text-gray-500 text-sm mt-1.5 font-medium">
              {selectedInstallment.title} - {selectedInstallment.amount}
            </p>

            <div className="w-full space-y-3 mt-7">
              <button
                type="button"
                onClick={() => handleProceedToCheckout()}
                className="w-full bg-[#00306b] hover:bg-[#002452] py-4 rounded-xl flex items-center justify-between shadow-md px-4 transition-colors cursor-pointer"
              >
                <span className="text-white text-lg font-bold">Kredi Kartı</span>
                <div className="bg-white px-2 py-1 rounded-lg flex items-center justify-center">
                  <img
                    src={cardLogos}
                    alt="Kredi Kartları"
                    className="h-5 object-contain"
                  />
                </div>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mt-5 text-gray-500 hover:text-gray-800 text-sm font-semibold transition-colors outline-none cursor-pointer"
            >
              İptal
            </button>
          </div>
        </div>
      )}
    </>
  );
}

